import { localized } from "../locales";
import type { Nullable } from "../types";
import type { calculateTimeBySeconds } from "./calculate-time-by-seconds";

/**
 * Formats a `{ hours, minutes, seconds }` object — typically the return value of
 * `calculateTimeBySeconds` — into a colon-separated time string with each
 * component zero-padded to at least two digits (e.g. `"01:02:03"`).
 *
 * Components longer than two characters are kept as-is, so values like
 * `123` hours render as `"123:00:00"` rather than being truncated.
 */
export function getCalculatedTimeString(
	params: Nullable<ReturnType<typeof calculateTimeBySeconds>>,
) {
	if (!params) return "00:00:00";
	const { hours, minutes, seconds } = params;
	return [hours, minutes, seconds].map((s) => s.toString().padStart(2, "0")).join(":");
}

/**
 * Formats a `{ hours, minutes, seconds }` object into a compact, human-readable
 * duration string using locale-aware abbreviations (e.g. `"8 min"`, `"15 h 8 min"`).
 *
 * Only hours and minutes are shown — seconds are ignored. When both components
 * are zero the result is `"0 min"`.
 *
 * @param params - The time components, typically from `calculateTimeBySeconds`. Pass
 *   `null` or `undefined` to receive the zero fallback `"0min"`.
 * @returns A compact duration string such as `"8 min"`, `"15 h"`, or `"15 h 8 min"`.
 */
export function getCalculatedTimeEasyString(
	params: Nullable<ReturnType<typeof calculateTimeBySeconds>>,
): string {
	const h = localized("dates.hours_abbr");
	const min = localized("dates.minutes_abbr");

	if (!params) return `0 ${min}`;

	const { hours, minutes } = params;

	if (hours > 0 && minutes > 0) return `${hours} ${h} ${minutes} ${min}`;
	if (hours > 0) return `${hours} ${h}`;
	return `${minutes} ${min}`;
}
